import { call, cancel, put, takeEvery, takeLatest } from 'redux-saga/effects';
import moment from 'moment';
import { initialize } from 'redux-form';

import {
  REPORTS_SHIFTS_CHANGE_DATE,
  REPORTS_SHIFTS_CHANGE_DATE_V2,
  REPORTS_SHIFTS_CHANGE_OUTLET,
  REPORTS_SHIFTS_CHANGE_OUTLET_V2,
  REPORTS_SHIFTS_DETAIL_HIDE,
  REPORTS_SHIFTS_DETAILS_REQUEST,
  REPORTS_SHIFT_OPEN_SHIFT_DETAILS,
  REPORTS_SHIFTS_EXPORT_SHIFT_REQUEST,
  REPORTS_SHIFTS_LIST_SHIFT_REQUEST,
  REPORTS_SHIFTS_PAGE_REQUEST,
  REPORTS_SHIFTS_PAGE_REQUEST_V2,
  REPORTS_SHIFTS_SHIFT_SUMMARY_REQUEST,
  REPORTS_SHIFTS_SUMMARY_LOAD_MORE_DATA,
} from '../../actions/reports/shifts/constants';
import { LOGOUT } from '../../actions/constants';
import { setCurrentOutlet } from '../../actions/outlet';
import {
  hideShiftDetail,
  listShiftRequest,
  setCurrentPage,
  setOutlets,
  setShiftDetails,
  setSelectedShift,
  setShifts,
  setShiftsV2,
  setShiftsV3,
  setSummary,
  shiftSummaryRequest,
  setPaymentMethodCategories,
  setPagination,
} from '../../actions/reports/shifts';
import { getExportedFile } from '../../actions/get-exported-file';
import * as actActionBar from '../../actions/actionBar';
import { showPageLoadingSpinner } from '../../actions/loadingSpinner';
import * as action from '../../actions';

import * as apiOutlet from '../../api/outlets';
import { getAllPaymentMethodCategories } from '../../api/paymentConfiguration';
import { DEFAULT_END_HOUR, DEFAULT_START_HOUR } from '../../utils/hourFilter';
import {
  getSelectedOutletIdFromLocalStorage,
  setSelectedOutletIdToLocalStorage,
} from '../../utils/selectedOutletId';
import * as clevertapReportsShifts from '../../clevertap/reports/shifts';
import { getFilteredDateAsMoment } from '../api/get-filtered-date';
import {
  exportShift,
  getShiftDetails,
  getShifts,
  getShiftsV2,
  getShiftsV3,
  getSummary,
} from '../api/cashdrawer/shifts';

function* getShiftSummaryAndShiftList(
  response,
  startDateFormatted,
  endDateFormatted
) {
  const paramOutletId = response.length > 1 ? '' : response.join();
  const currentOutlet = yield call(apiOutlet.outlets, { ids: paramOutletId });
  yield put(setCurrentOutlet(currentOutlet));

  yield put(
    listShiftRequest({
      page: 1,
      start: startDateFormatted,
      end: endDateFormatted,
      outletId: paramOutletId,
    })
  );
  yield put(
    shiftSummaryRequest({
      start: startDateFormatted,
      end: endDateFormatted,
      outletId: paramOutletId,
    })
  );
}

function* initForm(outlet, startDate, endDate) {
  const allDay = moment(endDate).format('HH:mm') === '23:59';
  const startHour = allDay
    ? DEFAULT_START_HOUR
    : moment(startDate).format('HH:mm');
  const endHour = allDay ? DEFAULT_END_HOUR : moment(endDate).format('HH:mm');

  yield put(
    initialize('search', {
      outlet,
      date: { startDate, endDate, startHour, endHour, allDay },
    })
  );
}

export function* initPaymentMethodCategories() {
  const paymentMethodCategories = yield call(getAllPaymentMethodCategories);
  yield put(setPaymentMethodCategories(paymentMethodCategories));
}

/**
 * Page flow
 */
export function* pageRequestFlow() {
  yield put(showPageLoadingSpinner(true));
  yield put(setShifts([]));
  yield put(setSummary({}));
  yield put(setCurrentPage(0));
  // reset shifts data
  yield put(setShiftDetails(null));

  const { startDate, endDate } = yield call(getFilteredDateAsMoment);
  const startDateFormatted = startDate.format('D/M/YYYY HH:mm:ss');
  const endDateFormatted = endDate.format('D/M/YYYY HH:mm:ss');
  // get profile and set it to dropdown outlet
  const outletIds = getSelectedOutletIdFromLocalStorage();
  yield call(
    getShiftSummaryAndShiftList,
    outletIds,
    startDateFormatted,
    endDateFormatted
  );

  yield call(initPaymentMethodCategories);

  const resOutlet = yield call(apiOutlet.outlets);
  yield put(setOutlets(resOutlet.data));

  // set actionbar state - used for connecting nav outlet and redux form value
  yield put(actActionBar.setOutlets(resOutlet.data));
  yield put(actActionBar.setSelectedOutletIds(outletIds));

  yield call(initForm, outletIds, startDate, endDate);
  clevertapReportsShifts.reportsShiftsStart({
    outletIds,
    startDate,
    endDate,
  });
}

export function* listShiftRequestFlowV2(query) {
  yield put(showPageLoadingSpinner(true));
  const { results: shifts, completed } = yield call(getShiftsV2, query);
  if (shifts) {
    yield put(setShiftsV2(shifts));
    yield put(
      setPagination({
        page: query.page,
        completed,
      })
    );
  }
  yield put(showPageLoadingSpinner(false));
}

export function* listShiftRequestFlowV3(query) {
  yield put(showPageLoadingSpinner(true));
  const { results: shifts, completed } = yield call(getShiftsV3, query);
  if (shifts) {
    yield put(setShiftsV3(shifts));
    yield put(
      setPagination({
        page: query.page,
        completed,
      })
    );
  }
  yield put(showPageLoadingSpinner(false));
}

export function* pageRequestFlowV2() {
  yield put(showPageLoadingSpinner(true));
  yield put(setShiftsV3([]));

  setPagination({
    page: 1,
    completed: true,
  });

  const { startDate, endDate } = yield call(getFilteredDateAsMoment);
  const since = startDate.valueOf();
  const until = endDate.valueOf();
  const outletIds = getSelectedOutletIdFromLocalStorage();
  const outletIdParams = outletIds.length > 1 ? '' : outletIds.join();
  const currentOutlet = yield call(apiOutlet.outlets, {
    ids: outletIdParams,
  });
  yield put(setCurrentOutlet(currentOutlet));

  yield call(listShiftRequestFlowV3, {
    page: 1,
    outletId: outletIdParams,
    since,
    until,
  });

  yield call(initPaymentMethodCategories);

  const resOutlet = yield call(apiOutlet.outlets);
  yield put(setOutlets(resOutlet.data));

  // set actionbar state - used for connecting nav outlet and redux form value
  yield put(actActionBar.setOutlets(resOutlet.data));
  yield put(actActionBar.setSelectedOutletIds(outletIds));

  yield call(initForm, outletIds, startDate, endDate);
  clevertapReportsShifts.reportsShiftsStart({
    outletIds,
    startDate,
    endDate,
  });

  yield put(showPageLoadingSpinner(false));
}

export function* changeOutletShift(request) {
  yield put(setShifts([]));
  yield put(setSummary({}));
  yield put(showPageLoadingSpinner(true));
  const {
    payload: { outletIds, startDate, endDate, isMobile },
  } = request;
  yield put(hideShiftDetail(isMobile));
  setSelectedOutletIdToLocalStorage(outletIds);
  yield call(initForm, outletIds, startDate, endDate);
  yield put(actActionBar.setSelectedOutletIds(outletIds));
  const startDateFormatted = moment(startDate).format('D/M/YYYY HH:mm:ss');
  const endDateFormatted = moment(endDate).format('D/M/YYYY HH:mm:ss');
  yield call(
    getShiftSummaryAndShiftList,
    getSelectedOutletIdFromLocalStorage(),
    startDateFormatted,
    endDateFormatted
  );
}

export function* changeOutletShiftV2(request) {
  yield put(showPageLoadingSpinner(true));
  yield put(setShiftsV3([]));

  const {
    payload: { outletIds: payloadOutletIds, startDate, endDate, isMobile },
  } = request;
  yield put(hideShiftDetail(isMobile));
  setSelectedOutletIdToLocalStorage(payloadOutletIds);
  yield call(initForm, payloadOutletIds, startDate, endDate);
  yield put(actActionBar.setSelectedOutletIds(payloadOutletIds));
  const since = moment(startDate).valueOf();
  const until = moment(endDate).valueOf();

  const outletIdsLocalStorage = getSelectedOutletIdFromLocalStorage();
  const outletIds =
    outletIdsLocalStorage.length > 1 ? '' : outletIdsLocalStorage.join();
  const currentOutlet = yield call(apiOutlet.outlets, { ids: outletIds });
  yield put(setCurrentOutlet(currentOutlet));

  yield call(listShiftRequestFlowV3, {
    page: 1,
    outletId: outletIds,
    since,
    until,
  });
}

export function* changeDateFlow(request) {
  yield put(setShifts([]));
  yield put(setSummary({}));
  yield put(showPageLoadingSpinner(true));
  const { startDate, endDate, isMobile, outlet } = request.payload;
  yield put(hideShiftDetail(isMobile));
  const startDateFormatted = moment(startDate).format('D/M/YYYY HH:mm:ss');
  const endDateFormatted = moment(endDate).format('D/M/YYYY HH:mm:ss');
  yield put(
    shiftSummaryRequest({
      start: startDateFormatted,
      end: endDateFormatted,
      outletId: outlet,
    })
  );
  yield put(
    listShiftRequest({
      page: 0,
      start: startDateFormatted,
      end: endDateFormatted,
      outletId: outlet,
    })
  );
}

export function* changeDateFlowV2(request) {
  yield put(setShiftsV3([]));

  yield put(showPageLoadingSpinner(true));
  const { startDate, endDate, isMobile, outlet } = request.payload;
  yield put(hideShiftDetail(isMobile));
  const startDateFormatted = moment(startDate).valueOf();
  const endDateFormatted = moment(endDate).valueOf();

  yield call(listShiftRequestFlowV3, {
    page: 1,
    since: startDateFormatted,
    until: endDateFormatted,
    outletId: outlet,
  });
}

/** *********************************
 * API CALL
 ********************************** */
export function* shiftSummaryRequestFlow(request) {
  const { query } = request.payload;
  const summary = yield call(getSummary, query);
  if (summary) {
    yield put(setSummary(summary));
  }
}

export function* listShiftRequestFlow(request) {
  yield put(showPageLoadingSpinner(true));
  const { query } = request.payload;
  const shifts = yield call(getShifts, query);
  if (shifts) {
    yield put(setShifts(shifts));
    yield put(setCurrentPage(query.page));
  }
  yield put(showPageLoadingSpinner(false));
}

export function* getShiftDetailFlow(request) {
  const { query, isMobile } = request.payload;
  // eslint-disable-next-line @typescript-eslint/naming-convention
  const outlet_id = getSelectedOutletIdFromLocalStorage();
  const shiftDetails = yield call(getShiftDetails, query, {
    outletId: outlet_id.length > 1 ? '' : outlet_id[0],
  });
  const { startDate, endDate } = yield call(getFilteredDateAsMoment);
  clevertapReportsShifts.reportsShiftsSelect({
    shift: {
      // eslint-disable-next-line @typescript-eslint/naming-convention
      outlet_id,
      // eslint-disable-next-line @typescript-eslint/naming-convention
      start_date: startDate,
      // eslint-disable-next-line @typescript-eslint/naming-convention
      end_date: endDate,
      ...shiftDetails,
    },
  });
  if (shiftDetails) {
    yield put(setShiftDetails(shiftDetails));
    if (!isMobile) {
      if ($('#mk-col-left').hasClass('col-md-12')) {
        $('#mk-col-left').removeClass('col-md-12').addClass('col-md-6');
        $('#mk-col-right').removeClass('col-md-12').addClass('col-md-6');
        setTimeout(() => {
          $('#mk-col-right').show();
          $('[data-toggle="popover"]').popover();
        }, 300);
      }
    }
  }
}

export function* setSelectedShiftFlow(request) {
  const { shift, isMobile } = request.payload;
  // eslint-disable-next-line @typescript-eslint/naming-convention
  const outlet_id = getSelectedOutletIdFromLocalStorage();
  const { startDate, endDate } = yield call(getFilteredDateAsMoment);
  clevertapReportsShifts.reportsShiftsSelect({
    shift: {
      // eslint-disable-next-line @typescript-eslint/naming-convention
      outlet_id,
      // eslint-disable-next-line @typescript-eslint/naming-convention
      start_date: startDate,
      // eslint-disable-next-line @typescript-eslint/naming-convention
      end_date: endDate,
      ...shift,
    },
  });
  if (shift) {
    yield put(setSelectedShift(shift));
    if (!isMobile) {
      if ($('#mk-col-left').hasClass('col-md-12')) {
        $('#mk-col-left').removeClass('col-md-12').addClass('col-md-6');
        $('#mk-col-right').removeClass('col-md-12').addClass('col-md-6');
        setTimeout(() => {
          $('#mk-col-right').show();
          $('[data-toggle="popover"]').popover();
        }, 300);
      }
    }
  }
}

export function* getMoreShiftData(params) {
  const { previousShift } = params.payload;

  const { endDate, page, startDate, outletId } = params.payload.query;

  yield put(action.showProgressBar());

  const since = moment(startDate).valueOf();
  const until = moment(endDate).valueOf();
  const query = {
    page,
    since,
    until,
    outletId,
  };
  const { results: shifts, completed } = yield call(getShiftsV3, query);
  yield put(setShiftsV3([...previousShift, ...shifts]));
  yield put(
    setPagination({
      page,
      completed,
    })
  );

  yield put(action.hideProgressBar());
  yield put(showPageLoadingSpinner(false));
}

export function* hideShiftDetailFlow(request) {
  const { isMobile } = request.payload;
  yield put(setShiftDetails(null));
  if (!isMobile) {
    // animation
    $('#mk-col-right').hide();
    $('#mk-col-left').removeClass('col-md-6').addClass('col-md-12');
  }
}

export function* exportShiftsFlow(request) {
  const {
    payload: { query, filename, outletId },
  } = request;
  query.outletId = outletId.length === 1 ? outletId[0] : '';
  const data = yield call(exportShift, query);
  if (data && data.message === 'ok') {
    yield put(getExportedFile(filename, outletId[0], 'shifts'));
  }
}

export function* cancelAction(cancelledTasks) {
  yield cancel(...cancelledTasks);
}

export default function* reportsShiftsSaga() {
  const summaryTask = yield takeLatest(
    REPORTS_SHIFTS_SHIFT_SUMMARY_REQUEST,
    shiftSummaryRequestFlow
  );
  const listShiftTask = yield takeLatest(
    REPORTS_SHIFTS_LIST_SHIFT_REQUEST,
    listShiftRequestFlow
  );
  const pageReqTask = yield takeLatest(
    REPORTS_SHIFTS_PAGE_REQUEST,
    pageRequestFlow
  );
  const pageReqTaskV2 = yield takeLatest(
    REPORTS_SHIFTS_PAGE_REQUEST_V2,
    pageRequestFlowV2
  );
  const changeDateTask = yield takeLatest(
    REPORTS_SHIFTS_CHANGE_DATE,
    changeDateFlow
  );
  const changeDateTaskV2 = yield takeLatest(
    REPORTS_SHIFTS_CHANGE_DATE_V2,
    changeDateFlowV2
  );
  const exportShiftTask = yield takeLatest(
    REPORTS_SHIFTS_EXPORT_SHIFT_REQUEST,
    exportShiftsFlow
  );
  const getShiftDetailTask = yield takeLatest(
    REPORTS_SHIFTS_DETAILS_REQUEST,
    getShiftDetailFlow
  );
  const setSelectedShiftTask = yield takeLatest(
    REPORTS_SHIFT_OPEN_SHIFT_DETAILS,
    setSelectedShiftFlow
  );
  const changeOutletTask = yield takeLatest(
    REPORTS_SHIFTS_CHANGE_OUTLET,
    changeOutletShift
  );
  const changeOutletTaskV2 = yield takeLatest(
    REPORTS_SHIFTS_CHANGE_OUTLET_V2,
    changeOutletShiftV2
  );
  const hideShiftDetailTask = yield takeLatest(
    REPORTS_SHIFTS_DETAIL_HIDE,
    hideShiftDetailFlow
  );
  const moreShiftData = yield takeLatest(
    REPORTS_SHIFTS_SUMMARY_LOAD_MORE_DATA,
    getMoreShiftData
  );

  yield takeEvery(LOGOUT, cancelAction, [
    summaryTask,
    listShiftTask,
    pageReqTask,
    pageReqTaskV2,
    changeOutletTask,
    changeOutletTaskV2,
    changeDateTask,
    changeDateTaskV2,
    exportShiftTask,
    getShiftDetailTask,
    setSelectedShiftTask,
    hideShiftDetailTask,
    moreShiftData,
  ]);
}
